// CSR-2063 — parsing an ACORD template into pages the editor can draw and
// blanks it can float inputs over.
//
// The pdf.js dependency is confined to this module and `AcordPdfForm`; the dialog
// above them only ever sees "loading / ready / failed", which is what the design
// spec's state matrix is written in.
import { useCallback, useEffect, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import {
  collectAcordPdfFields,
  collectAcordTemplateText,
  type AcordPdfField,
  type PdfAnnotationLike,
} from "./acord-pdf-fields";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

export interface AcordPdfPage {
  pageNumber: number;
  // Page units — the scale-1 viewport. The rendered size is this times the scale
  // the editor is displaying at.
  width: number;
  height: number;
  fields: AcordPdfField[];
  page: pdfjsLib.PDFPageProxy;
}

export interface AcordPdfDocumentState {
  pages: AcordPdfPage[];
  // Text the template already has in it, before the AMS prefill.
  templateText: Record<string, string>;
  isLoading: boolean;
  error: Error | null;
  reload: () => void;
}

export function useAcordPdfDocument(data: ArrayBuffer | undefined): AcordPdfDocumentState {
  const [pages, setPages] = useState<AcordPdfPage[]>([]);
  const [templateText, setTemplateText] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((current) => current + 1), []);

  useEffect(() => {
    if (!data) {
      setPages([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    let document: pdfjsLib.PDFDocumentProxy | null = null;
    setIsLoading(true);
    setError(null);

    (async () => {
      // A copy, not the cached buffer itself: pdf.js takes ownership of what it is
      // given and detaches it, which would leave the second open of the same form
      // parsing an empty buffer.
      const loadTask = pdfjsLib.getDocument({ data: new Uint8Array(data).slice() });
      document = await loadTask.promise;

      const parsed: AcordPdfPage[] = [];
      const printedText: Record<string, string> = {};

      // Every page, not just the first: ACORD 125/126/127 and the RPS Cargo App
      // are four pages, the W-9 six, and blanks live on most of them.
      for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber++) {
        const page = await document.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1 });
        const annotations = (await page.getAnnotations()) as PdfAnnotationLike[];
        if (cancelled) return;

        parsed.push({
          pageNumber,
          width: viewport.width,
          height: viewport.height,
          fields: collectAcordPdfFields(annotations, viewport, pageNumber),
          page,
        });

        Object.assign(printedText, collectAcordTemplateText(annotations));
      }

      setPages(parsed);
      setTemplateText(printedText);
      setIsLoading(false);
    })().catch((cause) => {
      if (cancelled) return;
      // The dialog turns this into the spec's generation-error state with a Retry
      // — never a silent blank page.
      console.error("ACORD template render failed:", cause);
      setError(cause instanceof Error ? cause : new Error("Failed to render the form"));
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
      document?.destroy();
    };
  }, [data, attempt]);

  return { pages, templateText, isLoading, error, reload };
}
